// Week — seven day columns × the 08:00–18:30 grid in 28px rows, for ONE route
// (tech / crew) picked in the header. A job row is [time] [name] [code] [$] on the
// service-family rail; a day's head opens that day's board. On the phone the week
// becomes a list grouped by day and a job opens a sheet.
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import useIsPhone from "../../../hooks/useIsPhone";
import SchedHeader from "./SchedHeader";
import JobSheet from "./JobSheet";
import useRangeBoard, { dayLabel, shiftIso, todayIso, weekDays, weekStartOf, weekRangeLabel } from "./useRangeBoard";
import { clean, formatCurrency, formatHours } from "../dispatch/utils";
import { serviceFamily } from "../dispatch/serviceFamily";
import { getDispatchVocab } from "../dispatch/dispatchVocab";
import "../RoutePlanner.css";
import "../dispatch/dispatch-b.css";
import "./sched-c.css";

const CustomerPopup = lazy(() => import("../../Profile/CustomerPopup"));
const SLOT_START = 8 * 60;
const SLOT_END = 18 * 60 + 30;
const toMin = (v) => { const [h, m] = String(v || "").split(":").map(Number); return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null; };
const label = (t) => `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
const routeKey = (r) => (clean(r.technicianId) ? `t:${clean(r.technicianId)}` : "unassigned");
const ROUTE_PREF = "schedule.week.route";

export default function WeekView() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const vocab = useMemo(() => getDispatchVocab(), []);
  const [start, setStart] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("date");
    return weekStartOf(/^\d{4}-\d{2}-\d{2}$/.test(p || "") ? p : todayIso());
  });
  const days = useMemo(() => weekDays(start), [start]);
  const { routes, loading, error } = useRangeBoard(days[0], days[6]);
  const [routePick, setRoutePick] = useState(() => { try { return localStorage.getItem(ROUTE_PREF) || ""; } catch { return ""; } });
  const [job, setJob] = useState(null);
  const [popup, setPopup] = useState(null);
  const today = todayIso();

  // The route choices across the week, busiest first; Unassigned last when it has jobs.
  const options = useMemo(() => {
    const by = new Map();
    routes.forEach((r) => {
      const k = routeKey(r);
      const cur = by.get(k) || { key: k, name: k === "unassigned" ? vocab.unassigned : r.technicianName || "—", stops: 0 };
      cur.stops += (r.stops || []).length;
      by.set(k, cur);
    });
    const list = [...by.values()].filter((o) => o.stops > 0 || o.key !== "unassigned");
    return list.sort((a, b) => (a.key === "unassigned") - (b.key === "unassigned") || b.stops - a.stops || a.name.localeCompare(b.name));
  }, [routes, vocab.unassigned]);
  const selected = options.find((o) => o.key === routePick)?.key || options[0]?.key || "";
  const pick = (k) => { setRoutePick(k); try { localStorage.setItem(ROUTE_PREF, k); } catch { /* private mode */ } };

  // The selected route's stops per day.
  const perDay = useMemo(() => {
    const out = Object.fromEntries(days.map((d) => [d, []]));
    routes.filter((r) => routeKey(r) === selected).forEach((r) => {
      const d = clean(r.serviceDay);
      if (!out[d]) return;
      out[d].push(...(r.stops || []).map((s) => ({ ...s, serviceDay: d, technicianName: clean(r.technicianId) ? r.technicianName : "" })));
    });
    Object.values(out).forEach((list) => list.sort((a, b) => (toMin(a.computedStart) ?? 0) - (toMin(b.computedStart) ?? 0)));
    return out;
  }, [routes, selected, days]);

  const all = useMemo(() => Object.values(perDay).flat(), [perDay]);
  const totals = {
    stops: all.length,
    minutes: all.reduce((n, s) => n + Number(s.durationMinutes || 0), 0),
    value: all.reduce((n, s) => n + Number(s.value || 0), 0),
  };
  useEffect(() => { setJob(null); }, [start, selected]);

  const openJob = (s) => {
    if (isPhone) { setJob(s); return; }
    if (s.customerId) { setPopup(s); return; }
    navigate(`/schedule/day?date=${s.serviceDay}`);
  };

  const weekLabel = weekRangeLabel(days[0], days[6]);

  const routeSelect = (
    <select className="sc-select" value={selected} onChange={(e) => pick(e.target.value)} aria-label={`Choose a ${vocab.worker}`} disabled={!options.length}>
      {options.length ? options.map((o) => <option key={o.key} value={o.key}>{o.name} · {o.stops}</option>) : <option value="">No {vocab.routes} this week</option>}
    </select>
  );

  const grid = (
    <div className="sc-week" role="grid" aria-label={`Week of ${weekLabel}`}>
      {days.map((d) => {
        const list = perDay[d] || [];
        const slots = new Map();
        for (let t = SLOT_START; t <= SLOT_END; t += 30) slots.set(t, []);
        list.forEach((s) => {
          const m = Math.min(Math.max(toMin(s.computedStart) ?? SLOT_START, SLOT_START), SLOT_END);
          slots.get(SLOT_START + Math.floor((m - SLOT_START) / 30) * 30).push(s);
        });
        const value = list.reduce((n, s) => n + Number(s.value || 0), 0);
        return (
          <div key={d} className="sc-week-col" role="rowgroup">
            <button type="button" className={`sc-week-head sc-link-head${d === today ? " is-today" : ""}`} onClick={() => navigate(`/schedule/day?date=${d}`)} title="Open the day board">
              <b>{dayLabel(d)}</b>
              <span>{list.length ? `${list.length} · ${formatCurrency(value)}` : "—"}</span>
            </button>
            <div className="dispatch-grid">
              {[...slots.entries()].map(([t, jobs]) => jobs.length ? (
                <div key={t} className="dispatch-grid-row has-jobs" role="row">
                  {jobs.map((s) => (
                    <button key={s.id} type="button" className="sc-job" style={{ "--pin": serviceFamily(s.serviceName, s.serviceCode).color }} onClick={() => openJob(s)} title={`${s.customerName} · ${s.serviceName || s.serviceCode || ""}${s.durationMinutes ? ` · ${s.durationMinutes} min` : ""}`}>
                      <span className="sc-job-time">{s.computedStart || label(t)}</span>
                      <span className="sc-job-name">{s.customerName}{s.serviceCode ? <small>{s.serviceCode}</small> : null}</span>
                      <span className="sc-job-value">{s.value ? formatCurrency(s.value) : ""}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div key={t} className="dispatch-grid-row is-empty is-static" role="row" aria-label={`${label(t)} open`}>
                  <span className="dispatch-grid-time">{label(t)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const list = (
    <div className="sc-list">
      {days.map((d) => {
        const items = perDay[d] || [];
        return (
          <React.Fragment key={d}>
            <div className={`sc-list-day${d === today ? " is-today" : ""}`}>
              <b>{new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</b>
              <span>{items.length ? `${items.length} · ${formatCurrency(items.reduce((n, s) => n + Number(s.value || 0), 0))}` : "—"}</span>
            </div>
            {items.map((s) => (
              <button key={s.id} type="button" className="sc-row" style={{ "--pin": serviceFamily(s.serviceName, s.serviceCode).color }} onClick={() => openJob(s)}>
                <span className="sc-row-time">{s.computedStart || ""}</span>
                <span className="sc-row-main"><b>{s.customerName}</b><span>{s.serviceName || s.serviceCode || ""}</span></span>
                <span className="sc-row-value">{s.value ? formatCurrency(s.value) : ""}</span>
                <ChevronRight size={14} className="sc-row-chev" aria-hidden="true" />
              </button>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="dispatch-shell sc-screen">
      <SchedHeader
        label={weekLabel}
        onPrev={() => setStart((s) => shiftIso(s, -7))}
        onNext={() => setStart((s) => shiftIso(s, 7))}
        prevLabel="Previous week"
        nextLabel="Next week"
        totals={loading ? [] : [`${totals.stops} ${vocab.stops}`, formatHours(totals.minutes), formatCurrency(totals.value)]}
        actions={isPhone ? null : routeSelect}
        menu={[{ label: "This week", run: () => setStart(weekStartOf(todayIso())) }]}
      />
      {isPhone ? <div className="sc-route-switch sc-route-switch--single">{routeSelect}</div> : null}
      {error ? <div className="sc-inline-error" role="alert">{error}</div> : null}
      {loading ? <div className="sc-loading">Loading the week…</div> : isPhone ? list : grid}
      {isPhone ? <JobSheet job={job} vocab={vocab} onClose={() => setJob(null)} /> : null}
      {popup ? (
        <Suspense fallback={null}>
          <CustomerPopup
            lead={{ object: "customer", id: popup.customerId, customer_id: popup.customerId, display_name: popup.customerName }}
            leadType="customer"
            initialTab="appointments"
            focusAppointment={popup.crmAppointmentId || popup.id}
            boardStartLabel={popup.computedStart || null}
            onClose={() => setPopup(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
